import { useState } from "react";
import { usePermissions } from "@/hooks/usePermissions";
import { useSnackbar } from "notistack";
import LoggingAPI from "@/api/logging";
import RelayAPI from "@/api/RelayAPI";
import { Button, Modal, useTheme } from "@monolith-forensics/monolith-ui";
import ButtonMenu from "@/components/Forms/components/ButtonMenu";

interface RemoveUserConfirmModalProps {
  show: boolean;
  onClose?: () => void;
  onSubmit?: (defaultInfo?: any) => void;
  onCancel?: () => void;
  buttonProps?: {
    cancelText: string;
    submitText: string;
  };
  defaultInfo: any; //
}

export const RemoveUserConfirmModal: React.FC<RemoveUserConfirmModalProps> = ({
  show,
  onClose,
  onSubmit,
  onCancel,
  buttonProps,
  defaultInfo,
}) => {
  const theme = useTheme();
  const [submitting, setSubmitting] = useState(false);
  const { currentUser } = usePermissions();
  const { enqueueSnackbar } = useSnackbar();

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await RelayAPI.removeUser({
        tenant_uuid: defaultInfo.tenant_uuid,
        user_id: defaultInfo.user_id,
      });

      enqueueSnackbar("User removed successfully", { variant: "success" });

      LoggingAPI.createLog({
        user_id: currentUser.user_id,
        message: `Removed Relay User: ${defaultInfo?.email}`,
        event: "relay:remove_user",
        object_type: "relay",
      });

      onSubmit?.(defaultInfo);
    } catch (error) {
      console.error("Error removing user:", error);
      enqueueSnackbar(`Error removing user.`, {
        variant: "error",
      });
    } finally {
      setSubmitting(false);
      onClose?.();
    }
  };

  const handleCancel = () => {
    onCancel?.();
  };

  return (
    <Modal open={show} onClose={onClose}>
      <Modal.Title>Remove Relay User</Modal.Title>
      <div style={{ fontWeight: 500, margin: "20px 0px" }}>
        Are you sure you want to remove user{" "}
        <span style={{ fontWeight: 800, color: theme.palette.primary.main }}>
          "{defaultInfo?.full_name}"
        </span>{" "}
        from your Relay Tenant?
        <p>
          This user will lose access to your Relay Tenant and all associated
          data.
        </p>
      </div>
      <ButtonMenu>
        <Button
          size="xs"
          variant="subtle"
          onClick={handleCancel}
          disabled={submitting}
        >
          {buttonProps?.cancelText || "Cancel"}
        </Button>
        <Button
          size="xs"
          color="error"
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting}
          loading={submitting}
        >
          {buttonProps?.submitText || "Remove User"}
        </Button>
      </ButtonMenu>
    </Modal>
  );
};
